// SPDX-License-Identifier: MIT
import { t } from '../i18n/t';
import { renderModeLadder, type Rung } from './mode-ladder';

// Inline desktop check — NOT imported from '../platform' on purpose : that barrel
// pulls the whole web+desktop bridge graph into every consumer (and their tests).
// Tauri 2 injects __TAURI_INTERNALS__ only in the desktop shell.
function isDesktopShell(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as Record<string, unknown>;
  return w.__TAURI_INTERNALS__ !== undefined || w.__TAURI__ !== undefined;
}
import { getEntitlement } from '../entitlements/api';

export interface HeaderUser {
  email: string;
  initial?: string;
}

export interface HeaderOptions {
  locale?: 'fr' | 'en';
  user?: HeaderUser;
  // When the header is rendered on /welcome, the page passes onSignOut so
  // the user-chip becomes a clickable affordance that triggers sign-out.
  // Without it (e.g. on the landing pre-auth), the chip is decorative only.
  onSignOut?: () => void | Promise<void>;
  activePath?: string;     // ex. '/facons' — marque le lien nav actif
  currentRung?: Rung;      // cran courant de l'échelle (défaut 'web')
  // Mobile drawer trigger for the /welcome app-shell. When provided, the
  // header renders a ☰ button (CSS-shown only ≤768px) that opens the
  // welcome sidebar drawer. Other routes don't pass this prop.
  onDrawerOpen?: () => void;
  // ✦ Mon espace pill (authed only) — shows the remaining-ideas count + links to
  // /compte. Pass a number to render it sync ; pass undefined → the header
  // fetches getEntitlement() async to fill it (degrades to "·"). null = skip fetch.
  ideasRemaining?: number | null;
}

export function renderHeader(opts: HeaderOptions = {}): HTMLElement {
  const locale = opts.locale ?? 'fr';
  const el = document.createElement('header');
  el.className = 'site-header';
  const userChip = opts.user ? renderUserChipHtml(opts.user, !!opts.onSignOut) : '';
  // 'Connecter' removed 2026-05-30 : it duplicated the ② Terminal rung (both went
  // to /connecter). Connecting is now per-mode via Façons (each mode card carries
  // its connect CTA) + managed in /compte › Connexions. /connecter stays a route
  // (reached from the Façons terminal card), just not a top-nav item. 5 → 4 entries.
  const NAV: ReadonlyArray<[href: string, key: string]> = [
    ['/welcome', 'nav.atelier'], ['/facons', 'nav.facons'],
    ['/tarifs', 'nav.tarifs'], ['/exemples', 'nav.exemples'],
  ];
  const navHtml = NAV.map(([href, key]) =>
    `<a data-nav href="${href}" class="site-nav__link${opts.activePath === href ? ' is-active' : ''}">${t(key)}</a>`
  ).join('')
  // « Partager » lives in the burger dropdown on mobile (the standalone .gx-share-bar
  // next to the logo is hidden ≤720px — it overflowed the tight header bar). Shown
  // only when the nav is the dropdown ; hidden on desktop where the standalone shows.
  + `<button type="button" data-gx-share class="site-nav__link gx-share-navitem">&#x2197;&nbsp;${t('share.cta')}</button>`;
  // The header is a vertical CHROME wrapper: a flex row (`.site-header__bar`)
  // carrying logo + nav + locale/chip, with the BYOAI mode-ladder appended
  // BELOW it as a full-width band (mock `.chrome__bar` + `.rungbar`). Keeping
  // the ladder OUT of the flex row is load-bearing — when it was a flex child
  // of the row it stretched the layout viewport on phones/tablets and forced
  // horizontal overflow (rollback 2026-05-27). See welcome-slim-v1.html.
  const drawerTriggerHtml = opts.onDrawerOpen
    ? `<button data-component="welcome-drawer-trigger" class="site-header__drawer-toggle" type="button" aria-label="${t('welcome.sidebar.openDrawer')}">☰</button>`
    : '';
  el.innerHTML = `
    <div class="site-header__bar">
      ${drawerTriggerHtml}
      <a href="${opts.user ? '/welcome' : '/'}" data-nav data-component="logo" class="site-header__logo" aria-label="${t('site.title')}" style="display:inline-flex;align-items:center;gap:8px;text-decoration:none;color:inherit;">
        <svg viewBox="0 0 64 64" width="26" height="26" aria-hidden="true" focusable="false">
          <rect x="14" y="14" width="36" height="36" transform="rotate(45 32 32)" fill="#faf6ed" stroke="#0f0f12" stroke-width="3.5"/>
          <rect x="20" y="20" width="24" height="24" transform="rotate(45 32 32)" fill="#6b4fff" stroke="#0f0f12" stroke-width="2.5"/>
        </svg>
        ${t('site.title')}
        <span class="site-logo-mit" style="font-family:var(--font-mono);font-size:10px;font-weight:700;color:#ff7043;background:rgba(255,112,67,0.12);padding:2px 6px;border-radius:3px;margin-left:4px;letter-spacing:0.05em;">MIT</span>
      </a>
      <nav id="site-nav" data-component="site-nav" class="site-nav">${navHtml}</nav>
      <div class="site-header__right">
        <div data-component="locale-toggle" class="locale-toggle">
          <button data-locale="fr" aria-pressed="${locale === 'fr'}">FR</button>
          <button data-locale="en" aria-pressed="${locale === 'en'}">EN</button>
        </div>
        ${opts.user ? `<a class="site-espace-pill" data-nav href="/compte" aria-label="${t('nav.monEspace')}"><span class="site-espace-pill__spark" aria-hidden="true">✦</span><b data-component="pill-ideas">${typeof opts.ideasRemaining === 'number' ? opts.ideasRemaining : '·'}</b><span class="site-espace-pill__txt">&nbsp;${t('nav.ideasShort')}</span></a>` : ''}
        ${userChip}
        <button data-component="nav-toggle" class="site-nav__toggle" aria-label="${t('nav.menu')}" aria-controls="site-nav" aria-expanded="false">
          <span class="site-nav__burger" aria-hidden="true"></span>
        </button>
      </div>
    </div>
  `;
  // (/exemples is now a first-class SPA route — keep its data-nav so the shared
  // chrome provides an exit and the page doesn't full-reload a 729 KB standalone.)

  // ✦ Mon espace pill : when the count wasn't passed in, fetch it async (authed
  // only) so every page's chrome shows the live ideas count. Best-effort.
  if (opts.user && opts.ideasRemaining === undefined) {
    const pillN = el.querySelector<HTMLElement>('[data-component="pill-ideas"]');
    if (pillN) void getEntitlement().then((e) => { pillN.textContent = String(Math.max(0, e.remaining)); }).catch(() => { /* leave "·" */ });
  }

  // Append the persistent BYOAI mode-ladder indicator below the header bar.
  // NOT in the desktop app : the ladder is a web→terminal→desktop ACQUISITION
  // funnel (convince a web visitor to climb). Once you're IN the desktop app you
  // ARE at the top — showing "tu es au cran ① Web" there is just confusing
  // (operator-reported 2026-05-30). Web keeps it ; desktop drops it.
  if (!isDesktopShell()) {
    el.appendChild(renderModeLadder({ current: opts.currentRung ?? 'web' }));
  }

  // Mobile menu: the hamburger toggles the nav (which CSS turns into a dropdown
  // below the bar at ≤720px). Without this, phones had no navigation at all.
  const navToggle = el.querySelector<HTMLButtonElement>('[data-component="nav-toggle"]');
  const nav = el.querySelector<HTMLElement>('[data-component="site-nav"]');
  if (navToggle && nav) {
    navToggle.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const open = nav.classList.toggle('is-open');
      navToggle.setAttribute('aria-expanded', String(open));
      if (open) {
        setTimeout(() => document.addEventListener('click', closeNavOnce, { once: true }), 0);
      }
    });
    // A nav link click closes the menu (SPA navigation stays on the same chrome).
    nav.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('a')) { nav.classList.remove('is-open'); navToggle.setAttribute('aria-expanded', 'false'); }
    });
  }
  function closeNavOnce(): void {
    nav?.classList.remove('is-open');
    navToggle?.setAttribute('aria-expanded', 'false');
  }

  // Welcome drawer trigger (mobile). When the page passes onDrawerOpen, the
  // ☰ button to the left of the logo fires it ; CSS hides the button on
  // desktop where the sidebar is always visible.
  if (opts.onDrawerOpen) {
    const onOpen = opts.onDrawerOpen;
    el.querySelector<HTMLButtonElement>('[data-component="welcome-drawer-trigger"]')
      ?.addEventListener('click', () => onOpen());
  }

  // Wire the chip's click → toggles a dropdown menu with a Sign-out item.
  // We render the menu on-demand on first click instead of inlining it so the
  // markup stays close to the chip and click-outside-to-close works simply.
  if (opts.user && opts.onSignOut) {
    const chip = el.querySelector<HTMLElement>('[data-component="user-chip"]')!;
    let menu: HTMLElement | null = null;
    const closeMenu = () => { menu?.remove(); menu = null; chip.setAttribute('aria-expanded', 'false'); };
    chip.setAttribute('role', 'button');
    chip.setAttribute('tabindex', '0');
    chip.setAttribute('aria-expanded', 'false');
    chip.setAttribute('aria-haspopup', 'menu');
    chip.style.cursor = 'pointer';
    const toggle = (ev: Event) => {
      ev.stopPropagation();
      if (menu) { closeMenu(); return; }
      menu = document.createElement('div');
      menu.className = 'user-chip__menu';
      menu.setAttribute('role', 'menu');
      menu.innerHTML = `
        <a data-nav href="/compte" class="user-chip__menu-item" role="menuitem">${t('nav.monEspace')}</a>
        <button type="button" data-gx-share class="user-chip__menu-item" role="menuitem">&#x2197;&nbsp; ${t('share.cta')}</button>
        <button data-component="user-menu-signout" type="button" class="user-chip__menu-item">
          ${t('footer.signOut')}
        </button>
      `;
      chip.parentElement!.style.position = chip.parentElement!.style.position || 'relative';
      chip.parentElement!.appendChild(menu);
      menu.querySelector<HTMLButtonElement>('[data-component="user-menu-signout"]')!.addEventListener('click', async (e) => {
        e.stopPropagation();
        closeMenu();
        await opts.onSignOut!();
      });
      chip.setAttribute('aria-expanded', 'true');
      // Click outside closes.
      setTimeout(() => {
        document.addEventListener('click', closeMenu, { once: true });
      }, 0);
    };
    chip.addEventListener('click', toggle);
    chip.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Enter' || k === ' ') { e.preventDefault(); toggle(e); }
      else if (k === 'Escape' && menu) { e.preventDefault(); closeMenu(); }
    });
  }

  return el;
}

function renderUserChipHtml(user: HeaderUser, clickable: boolean): string {
  const initial = (user.initial ?? user.email.charAt(0)).toUpperCase();
  const shortEmail = shorten(user.email);
  // When clickable (sign-out wired), the caret hints at a dropdown ; otherwise
  // hide it so the chip doesn't promise interactivity it doesn't have.
  const caret = clickable ? `<span class="user-chip__caret" aria-hidden="true">▾</span>` : '';
  return `
    <div data-component="user-chip" class="user-chip" aria-label="${escape(user.email)}">
      <div class="user-chip__avatar" aria-hidden="true">${escape(initial)}</div>
      <span class="user-chip__email">${escape(shortEmail)}</span>
      ${caret}
    </div>
  `;
}

function shorten(email: string): string {
  if (email.length <= 18) return email;
  const at = email.indexOf('@');
  if (at < 0) return email.slice(0, 15) + '…';
  const local = email.slice(0, at);
  if (local.length <= 12) return email;
  return local.slice(0, 10) + '…';
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
